import {
  type CohortBuilderDefinitionLegacy,
  type Criterion,
  type StudyArmDefinitionPayloadLegacy,
} from '@verantos-dev/types-cohorts';

/**
 * Transform criteria for backend payload.
 *
 * Since we now use camelCase properties with snake_case values consistently,
 * this function is simplified to just normalize legacy formats and ensure
 * property names match backend expectations (camelCase properties, snake_case values).
 *
 * FEATURE FLAG: This function handles the `analytical_variables_in_cohort_builder` feature flag.
 * When the feature flag is disabled, criteria will be undefined and this function will return undefined.
 *
 * CLEANUP TODO: When removing the `analytical_variables_in_cohort_builder` feature flag, ensure this function always handles criteria.
 */
export const transformCriteriaToPayload = (
  criteria: CohortBuilderDefinitionLegacy['criteria']
): StudyArmDefinitionPayloadLegacy['criteria'] => {
  if (!criteria) {
    return undefined;
  }

  return criteria.map((criterion): Criterion => {
    if (!criterion || typeof criterion !== 'object') {
      return criterion as Criterion;
    }

    const criterionObj = criterion as Record<string, unknown>;

    // Normalize legacy 'analyticalVariable' to 'analytical_variable'
    if ('criteriaType' in criterionObj) {
      const criteriaType = criterionObj.criteriaType as string;
      if (criteriaType === 'analyticalVariable') {
        return {
          ...criterion,
          criteriaType: 'analytical_variable',
        } as Criterion;
      }
    }

    // For all other criteria types, return as-is (already in correct format)
    return criterion as Criterion;
  });
};
